import React, { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
  Sparkles,
  ShieldCheck
} from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import useToast from "../hooks/useToast";
import { uploadFileToS3 } from "../services/uploadService";
import {
  createCommittee,
  deleteCommittee,
  getCommittees,
  updateCommittee
} from "../services/committeeService";

const committeeSchema = z.object({
  title: z.string().trim().min(1, "Title is required"),
  description: z.string().trim().optional(),
  file: z.string().trim().optional()
});

const defaultValues = {
  title: "",
  description: "",
  file: ""
};

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
  if (currentPage >= totalPages - 3) return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function Committee() {
  const [committees, setCommittees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });

  const fileInputPdfRef = useRef(null);
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues,
    resolver: zodResolver(committeeSchema)
  });

  const pdfUrl = watch("file");

  const refreshListing = (targetPage = pagination.page, targetLimit = pagination.limit) => {
    if (targetPage !== pagination.page || targetLimit !== pagination.limit) {
      setPagination((current) => ({ ...current, page: targetPage, limit: targetLimit }));
      return;
    }
    loadCommittees(targetPage, targetLimit);
  };

  async function loadCommittees(pageArg = pagination.page, limitArg = pagination.limit) {
    setIsLoading(true);
    const response = await getCommittees({ page: pageArg, limit: limitArg });
    if (response?.success) {
      setCommittees(response.data.committees || []);
      setPagination({
        page: response.data.pagination?.page || pageArg,
        limit: response.data.pagination?.limit || limitArg,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load Committees");
    }
    setIsLoading(false);
  }

  useEffect(() => {
    loadCommittees(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const openCreateModal = () => {
    reset(defaultValues);
    setEditingId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    reset(defaultValues);
    setEditingId(null);
    setIsModalOpen(false);
  };

  const handleFileUpload = (file) => {
    if (!file) return;

    if (file.type !== "application/pdf") {
      showErrorToast("Please upload a PDF file");
      return;
    }

    setIsUploadingPdf(true);
    uploadFileToS3(file, "committee").then((response) => {
      if (!response?.success) {
        showErrorToast(response?.message || "Unable to upload PDF");
        setIsUploadingPdf(false);
        return;
      }

      setValue("file", response.data.file.url, { shouldDirty: true, shouldValidate: true });
      setIsUploadingPdf(false);
      showSuccessToast("PDF uploaded successfully");
    });
  };

  const handleEdit = (committee) => {
    setEditingId(committee.id);
    reset({
      title: committee.title,
      description: committee.description || "",
      file: committee.file || ""
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    const response = await deleteCommittee(pendingDelete.id);
    setIsDeleting(false);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to delete committee");
      return;
    }

    const nextPage = committees.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
    showSuccessToast(response.message || "Committee deleted successfully");
    setPendingDelete(null);
    refreshListing(nextPage, pagination.limit);
  };

  const onSubmit = async (values) => {
    const payload = {
      ...values,
      file: values.file || ""
    };
    const response = editingId
      ? await updateCommittee(editingId, payload)
      : await createCommittee(payload);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to save committee");
      return;
    }

    showSuccessToast(response.message || "Committee saved successfully");
    closeModal();
    if (editingId) refreshListing(pagination.page, pagination.limit);
    else refreshListing(1, pagination.limit);
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="NMC Info"
        title="Committees"
        description="Manage committee details, member lists, and related documents."
        badge={`${pagination.total} Records`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Committees</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">View records and manage entries with paginated control.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button type="button" onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" /> Add Committee
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border p-6 text-sm text-panel-muted">Loading entries...</div>
          ) : committees.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="bg-slate-50/90">
                      <tr className="text-left">
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Title</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Description</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Attached PDF</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {committees.map((item) => (
                        <tr key={item.id} className="border-t border-slate-100 align-top">
                          <td className="px-5 py-4 text-sm font-semibold text-panel-ink">{item.title}</td>
                          <td className="px-5 py-4 text-sm text-panel-muted">{item.description || "--"}</td>
                          <td className="px-5 py-4 text-sm text-panel-muted">
                            {item.file ? (
                              <a href={item.file} target="_blank" rel="noreferrer" className="inline-flex items-center text-primary underline gap-1">
                                <FileText size={14} /> View File
                              </a>
                            ) : (
                              "--"
                            )}
                          </td>
                          <td className="px-5 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => handleEdit(item)}>
                                <Pencil className="mr-1 h-4 w-4" /> Edit
                              </Button>
                              <Button type="button" size="sm" variant="ghost" className="hover:bg-rose-50 hover:text-rose-600 text-panel-muted" onClick={() => setPendingDelete(item)}>
                                <Trash2 className="mr-1 h-4 w-4" /> Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-panel-muted">
                  Showing <span className="font-semibold text-panel-ink">{startItem}</span> to <span className="font-semibold text-panel-ink">{endItem}</span> of <span className="font-semibold text-panel-ink">{pagination.total}</span> committees
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => refreshListing(pagination.page - 1)}>Prev</Button>
                  {pageNumbers.map((p, i) => (
                    p === "..." ? <span key={i} className="px-1 text-sm">...</span> :
                    <Button key={i} type="button" size="sm" variant={pagination.page === p ? "default" : "outline"} onClick={() => refreshListing(p)}>{p}</Button>
                  ))}
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => refreshListing(pagination.page + 1)}>Next</Button>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 p-8 text-center">
              <ShieldCheck className="mx-auto h-10 w-10 text-primary/20 mb-4" />
              <p className="text-lg font-semibold">No committees added yet.</p>
              <Button type="button" className="mt-4" onClick={openCreateModal}><Plus className="mr-2 h-4 w-4" /> Add Your First Committee</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 overflow-y-auto">
          <div className="max-h-[90dvh] w-full max-w-xl overflow-y-auto rounded-[32px] border bg-white shadow-xl p-6">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h2 className="text-2xl font-bold">{editingId ? "Edit Committee" : "Add Committee"}</h2>
              <button type="button" onClick={closeModal}><X /></button>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-2">
                <FieldLabel>Title *</FieldLabel>
                <Input placeholder="Enter title (e.g. Anti-Ragging Committee)" {...register("title")} />
                {errors.title && <FieldError>{errors.title.message}</FieldError>}
              </div>

              <div className="space-y-2">
                <FieldLabel>Description</FieldLabel>
                <Textarea placeholder="Enter details or description" {...register("description")} />
              </div>

              <div className="space-y-2 border p-4 rounded-xl bg-slate-50">
                <FieldLabel>Attachment PDF</FieldLabel>
                <input ref={fileInputPdfRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => handleFileUpload(e.target.files?.[0])} />
                <div className="flex items-center gap-3">
                  <Button type="button" variant="outline" onClick={() => fileInputPdfRef.current?.click()} disabled={isUploadingPdf}>
                    <Upload className="mr-2 h-4 w-4" /> {isUploadingPdf ? "Uploading..." : "Upload PDF"}
                  </Button>
                  {pdfUrl ? (
                    <div className="flex items-center gap-2 text-sm text-primary">
                      <FileText size={16} /> <a href={pdfUrl} target="_blank" rel="noreferrer" className="underline">View PDF</a>
                      <Button type="button" variant="ghost" size="sm" className="text-rose-500 ml-2" onClick={() => setValue("file", "")} disabled={isUploadingPdf}>Remove</Button>
                    </div>
                  ) : <span className="text-xs text-panel-muted">Only PDF files accepted (Optional)</span>}
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button type="submit" disabled={isSubmitting || isUploadingPdf}>
                  {isSubmitting ? "Saving..." : editingId ? "Update Committee" : "Save Committee"}
                </Button>
                <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-[32px] border bg-white p-6 shadow-xl text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 mx-auto mb-4">
              <Trash2 className="h-7 w-7" />
            </div>
            <h2 className="text-2xl font-bold">Delete Committee?</h2>
            <p className="mt-3 text-panel-muted">Deleting <span className="font-bold text-panel-ink">{pendingDelete.title}</span> is permanent.</p>
            <div className="mt-8 flex gap-4">
              <Button type="button" variant="destructive" className="flex-1" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Confirm Delete"}
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setPendingDelete(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
